import { useToast } from '@components/index'
import { apiClient } from '@services/api-client'
import { SNA_PREFIX } from '@services/utils'
import { useOnError } from '@services/utils/onError'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { rentAmendmentsQueryKey } from '../queries/useRentAmendments'
import type { RentAmendment } from '../types'

function useAmendmentAction(action: 'cancel' | 'refresh', toastKey: string) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation('toast')
  const onError = useOnError()

  async function run(id: string) {
    const res = await apiClient.post<RentAmendment>(
      `${SNA_PREFIX}rent-amendments/${id}/${action}`
    )
    return res.data
  }

  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      toast.success({ message: t(toastKey) })
      queryClient.invalidateQueries({ queryKey: rentAmendmentsQueryKey })
      // Applying an amendment rewrites the offer/flat amounts and the rents.
      queryClient.invalidateQueries({ queryKey: ['offers-detail'] })
      queryClient.invalidateQueries({ queryKey: ['offers'] })
      queryClient.invalidateQueries({ queryKey: ['flats'] })
      queryClient.invalidateQueries({ queryKey: ['rents'] })
      // The signed PDF is stored among the contract's attachments.
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
    },
    onError
  })
}

export const useCancelRentAmendment = () =>
  useAmendmentAction('cancel', 'rentAmendmentCancelled')

export const useRefreshRentAmendment = () =>
  useAmendmentAction('refresh', 'rentAmendmentRefreshed')
